import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { customersApi, type Customer360 } from '../api/customers.api.js';
import { PROVIDER_ONLY_TYPES, documentTypeLabel, fileableTypes } from './document-types.js';
import { APP_LOCALE } from '../utils/locale.js';

type CustomerDocument = NonNullable<Customer360['documents']>[number];

/**
 * One document on the client card: the file itself beside what we know about
 * it, and the two things an officer keeps current -- its type and the dates it
 * turns on (DEV-98). The same kind of look the application review gives.
 *
 * Dates are optional. With none the document does not expire; an expiry date
 * (or, for a proof, an issue date) starts the clock.
 */
export function CustomerDocumentDialog({
  customerId,
  document,
  isBusiness,
  canEdit,
  onClose,
  onChanged,
}: {
  readonly customerId: string;
  readonly document: CustomerDocument;
  readonly isBusiness: boolean;
  readonly canEdit: boolean;
  readonly onClose: () => void;
  readonly onChanged: () => void;
}): JSX.Element {
  const hasFile = document.origin === 'VAULT';
  const [url, setUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState('');
  const [loading, setLoading] = useState(hasFile);
  const [loadError, setLoadError] = useState('');
  const [type, setType] = useState(document.documentType);
  const [issuedAt, setIssuedAt] = useState(document.issuedAt?.slice(0, 10) ?? '');
  const [expiresAt, setExpiresAt] = useState(document.expiresAt?.slice(0, 10) ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // The bytes, fetched with the officer's session; the object URL is revoked
  // when the dialog closes so a long review does not pile up blobs.
  useEffect(() => {
    if (!hasFile) return () => undefined;
    let cancelled = false;
    let created: string | null = null;
    customersApi.fetchDocumentBlob(customerId, document.id)
      .then((blob) => {
        if (cancelled) return;
        created = URL.createObjectURL(blob);
        setUrl(created);
        setContentType(blob.type);
      })
      .catch((err: unknown) => { if (!cancelled) setLoadError(err instanceof Error ? err.message : 'The file could not be opened.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => {
      cancelled = true;
      if (created !== null) URL.revokeObjectURL(created);
    };
  }, [customerId, document.id, hasFile]);

  const retypeable = hasFile && canEdit && !PROVIDER_ONLY_TYPES.has(document.documentType);
  const types = fileableTypes(isBusiness);
  const typeOptions = types.some(([value]) => value === document.documentType)
    ? types
    : [[document.documentType, documentTypeLabel(document.documentType)] as const, ...types];
  const datesChanged = issuedAt !== (document.issuedAt?.slice(0, 10) ?? '') || expiresAt !== (document.expiresAt?.slice(0, 10) ?? '');
  const typeChanged = type !== document.documentType;
  const today = new Date().toISOString().slice(0, 10);
  const datesInvalid = (issuedAt !== '' && issuedAt > today) || (issuedAt !== '' && expiresAt !== '' && expiresAt < issuedAt);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError('');
    try {
      if (typeChanged) await customersApi.retypeDocument(customerId, document.id, type);
      if (datesChanged) {
        await customersApi.setDocumentDates(customerId, document.id, {
          issuedAt: issuedAt === '' ? null : issuedAt,
          expiresAt: expiresAt === '' ? null : expiresAt,
        });
      }
      onChanged();
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const download = (): void => {
    if (url === null) return;
    const anchor = window.document.createElement('a');
    anchor.href = url;
    anchor.download = document.name ?? 'document';
    window.document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const isImage = contentType.startsWith('image/');
  const isPdf = contentType === 'application/pdf';

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" aria-labelledby="customer-document-title">
      <DialogTitle id="customer-document-title" sx={{ pr: 6 }}>
        {document.name ?? documentTypeLabel(document.documentType)}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 320px' }, gap: 3 }}>
          <Box
            aria-label="Document preview"
            sx={{ minHeight: { xs: 320, md: 560 }, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
          >
            {!hasFile ? (
              <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
                This record came from the identity provider without a file held here.
              </Typography>
            ) : loading ? (
              <CircularProgress size={28} />
            ) : loadError !== '' ? (
              <Alert severity="warning" sx={{ m: 2 }}>{loadError}</Alert>
            ) : url !== null && isImage ? (
              <Box component="img" src={url} alt={document.name ?? 'Document'} sx={{ maxWidth: '100%', maxHeight: 640, objectFit: 'contain' }} />
            ) : url !== null && isPdf ? (
              <Box component="iframe" src={url} title={document.name ?? 'Document'} sx={{ width: '100%', height: { xs: 420, md: 640 }, border: 0 }} />
            ) : (
              <Stack spacing={1} alignItems="center" sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">This file type cannot be shown here.</Typography>
                <Button size="small" variant="outlined" onClick={download}>Download it</Button>
              </Stack>
            )}
          </Box>

          <Stack spacing={2}>
            {error !== '' ? <Alert severity="error">{error}</Alert> : null}
            <Box>
              <Typography variant="caption" color="text.secondary">Validity</Typography>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <ValidityChip validity={document.validity} />
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>{document.validityDetail}</Typography>
            </Box>

            {retypeable ? (
              <TextField select size="small" label="Document type" value={type} onChange={(e) => setType(e.target.value)}>
                {typeOptions.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </TextField>
            ) : (
              <Box>
                <Typography variant="caption" color="text.secondary">Document type</Typography>
                <Typography variant="body2">{documentTypeLabel(document.documentType)}</Typography>
              </Box>
            )}

            {hasFile && canEdit ? (
              <>
                <TextField
                  size="small" type="date" label="Valid until (optional)" value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)} InputLabelProps={{ shrink: true }}
                  helperText="Leave empty and the document does not expire."
                />
                <TextField
                  size="small" type="date" label="Issued on (optional)" value={issuedAt}
                  onChange={(e) => setIssuedAt(e.target.value)} InputLabelProps={{ shrink: true }}
                  inputProps={{ max: today }}
                  error={datesInvalid}
                  helperText={datesInvalid ? 'The issue date must be today or earlier, and before the validity date.' : 'For a proof of address, starts its acceptance window.'}
                />
                {(issuedAt !== '' || expiresAt !== '') ? (
                  <Button size="small" sx={{ alignSelf: 'flex-start' }} onClick={() => { setIssuedAt(''); setExpiresAt(''); }}>
                    Clear dates (no expiry)
                  </Button>
                ) : null}
              </>
            ) : null}

            <Box>
              <Typography variant="caption" color="text.secondary">Status</Typography>
              <Typography variant="body2">{document.status}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">Collected</Typography>
              <Typography variant="body2">{new Date(document.collectedAt).toLocaleDateString(APP_LOCALE)}</Typography>
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        {url !== null ? <Button onClick={download}>Download</Button> : null}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={onClose} disabled={saving}>{hasFile && canEdit ? 'Cancel' : 'Close'}</Button>
        {hasFile && canEdit ? (
          <Button variant="contained" disabled={saving || datesInvalid || (!datesChanged && !typeChanged)} onClick={() => { void save(); }}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export function ValidityChip({ validity }: { readonly validity: string }): JSX.Element {
  const label = validity === 'NO_EXPIRY' ? 'No expiry' : validity.charAt(0) + validity.slice(1).toLowerCase();
  const color = validity === 'VALID' ? 'success' : validity === 'NO_EXPIRY' ? 'default' : validity === 'EXPIRING' ? 'warning' : 'error';
  return <Chip size="small" label={label} color={color} variant={validity === 'NO_EXPIRY' ? 'outlined' : 'filled'} />;
}
